/**
 * Fachliche Aufzaehlungen.
 * Bewusst als const-Objekte + Union-Typen, damit sie zur Laufzeit (Validierung,
 * UI-Listen) und zur Compile-Zeit (Typsicherheit) verwendet werden koennen.
 */
export const TASK_STATUS = ['OPEN', 'IN_PROGRESS', 'WAITING', 'DONE', 'CANCELLED'];
export const OPEN_TASK_STATUS = ['OPEN', 'IN_PROGRESS', 'WAITING'];
export const TASK_PRIORITY = ['LOW', 'NORMAL', 'HIGH', 'URGENT'];
export const ORG_UNIT_TYPE = ['CLUB', 'BOARD', 'SECTION', 'GROUP', 'TEAM', 'COMMITTEE', 'OTHER'];
export const EVENT_TYPE = [
    'MATCH',
    'TRAINING',
    'MEETING',
    'EVENT',
    'TASK_DEADLINE',
    'SPONSOR',
    'OTHER',
];
export const SOURCE_OF_TRUTH = ['UK_INFO', 'GOOGLE', 'FOOTBALL_FEDERATION'];
export const SYNC_STATUS = ['NONE', 'PENDING', 'SYNCED', 'CONFLICT', 'ERROR'];
export const MATCH_STATUS = ['SCHEDULED', 'POSTPONED', 'CANCELLED', 'PLAYED'];
export const SPONSOR_STATUS = ['PROSPECT', 'ACTIVE', 'PAUSED', 'ENDED'];
/** Ob der Sponsor fuer weitere Anfragen (z. B. Events) angesprochen werden darf. */
export const SPONSOR_FURTHER_REQUESTS = ['OPEN', 'VIA_MANAGER', 'NO'];
/** Ablauf einer Sponsor-Anfrage: geplant -> angefragt -> zu- oder abgesagt. */
export const SPONSOR_REQUEST_STATUS = ['PLANNED', 'REQUESTED', 'ACCEPTED', 'DECLINED', 'CANCELLED'];
export const OPEN_SPONSOR_REQUEST_STATUS = ['PLANNED', 'REQUESTED'];
export const SPONSORSHIP_STATUS = ['DRAFT', 'ACTIVE', 'EXPIRING', 'ENDED', 'CANCELLED'];
export const BENEFIT_TYPE = [
    'BANDENWERBUNG',
    'TRIKOTLOGO',
    'WEBSITE',
    'SOCIAL_MEDIA',
    'MATCHSPONSOR',
    'PLAKAT',
    'TURNIERWERBUNG',
    'VIP_EINLADUNG',
    'DURCHSAGE',
    'SONSTIGES',
];
export const BENEFIT_STATUS = ['OPEN', 'IN_PROGRESS', 'DELIVERED', 'CANCELLED'];
export const MEDIA_POST_STATUS = ['IDEA', 'DRAFT', 'REVIEW', 'APPROVED', 'PUBLISHED', 'ARCHIVED'];
export const MEDIA_CHANNEL = ['INSTAGRAM', 'FACEBOOK', 'WEBSITE', 'NEWSLETTER', 'PRESSE', 'WHATSAPP'];
export const MEDIA_KIND = ['PHOTO', 'VIDEO', 'LOGO', 'GRAPHIC', 'TEMPLATE', 'DOCUMENT', 'OTHER'];
/** Herkunft eines Anhangs: Mediathek oder Wissen & Dokumente. */
export const ATTACHMENT_KIND = ['MEDIA', 'DOCUMENT'];
export const DOCUMENT_CATEGORY = [
    'STATUTEN',
    'PROTOKOLL',
    'FORMULAR',
    'ANLEITUNG',
    'CHECKLISTE',
    'SPONSORING',
    'TURNIER',
    'RICHTLINIE',
    'TRAINING',
    'SONSTIGES',
];
export const VISIBILITY = ['UNIT', 'CLUB', 'PUBLIC'];
export const STORAGE_PROVIDER = ['LOCAL', 'EXTERNAL_LINK', 'GOOGLE_DRIVE', 'R2', 'SUPABASE'];
export const NOTIFICATION_TYPE = [
    'TASK_ASSIGNED',
    'TASK_UNASSIGNED',
    'TASK_DUE_TOMORROW',
    'TASK_OVERDUE',
    'TASK_COMMENT',
    'DOCUMENT_APPROVAL',
    'MEDIA_APPROVAL',
    'SPONSORSHIP_EXPIRING',
    'MATCH_CHANGED',
    'SPORT_RESPONSE_DUE',
    'SPORT_ATTENDANCE_UNCONFIRMED',
    'PERMISSION_CHANGED',
    'SPONSOR_REQUEST',
    'POLL_REQUESTED',
    'TRAINING_REMARK',
];
export const AUTOMATION_TRIGGER = ['MATCH_CREATED', 'MATCH_CHANGED', 'SPONSORSHIP_EXPIRING'];
export const RECURRENCE = ['NONE', 'DAILY', 'WEEKLY', 'MONTHLY', 'YEARLY'];
/** Rueckmeldung vor dem Termin. */
export const RSVP = ['YES', 'NO', 'MAYBE', 'PENDING'];
/** Was ohne Antwort gilt: offen oder zugesagt (nur Absagen melden). */
export const DEFAULT_RSVP = ['PENDING', 'YES'];
/** Anwesenheit nach dem Termin. */
export const ATTENDANCE = ['PRESENT', 'ABSENT', 'EXCUSED', 'UNKNOWN'];
/**
 * Grund einer Absage oder eines Fehlens.
 *
 * Bewusst eine feste Liste statt nur Freitext: nur so laesst sich auswerten,
 * wer verletzt ist oder wie oft beruflich abgesagt wird. Fuer alles Weitere
 * gibt es zusaetzlich eine Notiz.
 */
export const ABSENCE_REASON = [
    'NONE',
    'INJURED',
    'ILL',
    'WORK',
    'SCHOOL',
    'VACATION',
    'FAMILY',
    'OTHER',
];
/** Gruende, die ein Fehlen erklaeren - "NONE" heisst: kein Grund angegeben. */
export const NAMED_ABSENCE_REASONS = ABSENCE_REASON.filter((reason) => reason !== 'NONE');
/**
 * Trainingsplanung: Art einer Uebung und Phase einer Trainingseinheit.
 * Eine Einheit gliedert sich in Aufwaermen, Hauptteil und Schlussteil.
 */
export const EXERCISE_CATEGORY = [
    'WARMUP',
    'TECHNIQUE',
    'TACTICS',
    'GAME_FORM',
    'ATHLETICS',
    'GOALKEEPER',
    'COOLDOWN',
];
export const EXERCISE_CATEGORY_LABELS = {
    WARMUP: 'Aufwärmen',
    TECHNIQUE: 'Technik',
    TACTICS: 'Taktik',
    GAME_FORM: 'Spielform',
    ATHLETICS: 'Athletik',
    GOALKEEPER: 'Torhüter',
    COOLDOWN: 'Ausklang',
};
export const PLAN_PHASE = ['WARMUP', 'MAIN', 'FINAL'];
export const PLAN_PHASE_LABELS = {
    WARMUP: 'Aufwärmen',
    MAIN: 'Hauptteil',
    FINAL: 'Schlussteil',
};
/**
 * Art eines Termins mit Anwesenheitsliste. `WORK` ist der Arbeitseinsatz:
 * Platzpflege, Zeltfest, Aufbau - gefuehrt wie ein Training, zaehlt aber
 * nicht in die Trainingsteilnahme.
 */
export const SESSION_KIND = ['TRAINING', 'MATCH', 'WORK'];
/**
 * Vorgabe fuer die Rueckmeldefrist: so viele Stunden vor Beginn sollen die
 * Antworten da sein. Je Mannschaft und Terminart einstellbar.
 */
export const DEFAULT_RESPONSE_DEADLINE_HOURS = { MATCH: 48, TRAINING: 24, WORK: 24 };
/**
 * Push an die Mannschaftsleitung, wenn jemand seine Rueckmeldung aendert.
 * Je Terminart und Person einschaltbar; ohne Einstellung aus, damit
 * Vereinsverantwortliche mit Rechten auf alle Mannschaften nicht ueberflutet
 * werden.
 */
export const RESPONSE_PUSH_TYPE = {
    MATCH: 'SPORT_RESPONSE_CHANGED_MATCH',
    TRAINING: 'SPORT_RESPONSE_CHANGED_TRAINING',
    WORK: 'SPORT_RESPONSE_CHANGED_WORK',
};
/**
 * Stellung einer Person im Kader.
 *
 * `OCCASIONAL` steht fuer inaktive Personen und solche, die nur gelegentlich
 * aushelfen. Sie bleiben Teilnehmende und koennen zu- oder absagen, werden in
 * Uebersichten und Auswertungen aber standardmaessig ausgeblendet - sonst
 * stuende die halbe Liste dauerhaft auf "offen".
 */
export const ROSTER_STATUS = ['ACTIVE', 'OCCASIONAL'];
/**
 * Bereich einer Mannschaft. Erwachsenenmannschaften desselben Bereichs sind
 * untereinander durchlaessig (KM und 1b der Herren), Damen und Herren nicht.
 * Bewusst an der Mannschaft und nicht als Geschlecht an der Person.
 */
export const TEAM_DIVISION = ['MEN', 'WOMEN', 'MIXED'];
/**
 * Teilnahme einer Person als Band statt als nackte Prozentzahl.
 *
 * Entschuldigtes Fehlen (Verletzung, Krankheit, eingetragene Abwesenheit)
 * zaehlt nicht mit: das Band beantwortet "von den Terminen, bei denen du
 * konntest, warst du wie oft da?". Wer die meiste Zeit entschuldigt gefehlt
 * hat, bekommt gar kein Band, sondern "Pause" - und wer noch zu wenige
 * gewertete Termine hat, "zu wenig Daten". Sonst macht eine einzige Absage
 * sofort Rot.
 */
export const ATTENDANCE_BAND = ['GREAT', 'GOOD', 'FAIR', 'LOW', 'PAUSED', 'UNKNOWN'];
export const ATTENDANCE_BAND_LABELS = {
    GREAT: 'sehr gut dabei',
    GOOD: 'gut dabei',
    FAIR: 'ausbaufähig',
    LOW: 'selten dabei',
    PAUSED: 'Pause',
    UNKNOWN: 'zu wenig Daten',
};
/** Ab so vielen gewerteten Terminen gibt es ein Band. */
export const ATTENDANCE_BAND_MIN = 5;
export function attendanceBand({ present, counted, excused }) {
    if (excused > (counted + excused) / 2)
        return 'PAUSED';
    if (counted < ATTENDANCE_BAND_MIN)
        return 'UNKNOWN';
    const quote = (present / counted) * 100;
    if (quote >= 90)
        return 'GREAT';
    if (quote >= 75)
        return 'GOOD';
    if (quote >= 60)
        return 'FAIR';
    return 'LOW';
}
/** Wie ein einzelner Termin in der Teilnahme zaehlt. */
export const ATTENDANCE_STATE = ['PRESENT', 'EXCUSED', 'ABSENT', 'UNKNOWN'];
//# sourceMappingURL=enums.js.map